/**
 * Google Maps Places & Live Europe Geocode Service
 * 
 * Provides live, real-time place suggestions across Salzburg, Austria, and all of Europe.
 * Zero mock locations.
 * 
 * Mutual Salzburg Base Rule:
 * - At least one of FROM or TO must be in Salzburg.
 * - If user picks a Salzburg location for one, the other can be ANYWHERE on the map (all across Europe).
 * - If user picks an outside-Salzburg location for one, the other must be strictly inside Salzburg.
 * - For hourly driver, pickup must be inside Salzburg.
 */

import {
  SALZBURG_BOUNDS,
  SALZBURG_CENTER,
  isLocationInSalzburg,
  type LocationGeoData,
} from '../utils/salzburgLocation'

declare global {
  interface Window {
    google?: any
    __googleMapsLoadingPromise?: Promise<boolean>
  }
}

let autocompleteServiceInstance: any = null
let placesServiceInstance: any = null

/**
 * Asynchronously loads the Google Maps JavaScript API with Places library if an API key is provided.
 */
export async function loadGoogleMapsScript(): Promise<boolean> {
  if (typeof window === 'undefined') return false

  if (window.google?.maps?.places) {
    return true
  }

  if (window.__googleMapsLoadingPromise) {
    return window.__googleMapsLoadingPromise
  }

  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim()

  window.__googleMapsLoadingPromise = new Promise<boolean>((resolve) => {
    // If no key is set, rely on the fast live European geocoding engine
    if (!apiKey) {
      resolve(false)
      return
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(checkLoaded)
          resolve(true)
        }
      }, 100)
      setTimeout(() => {
        clearInterval(checkLoaded)
        resolve(Boolean(window.google?.maps?.places))
      }, 4000)
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&loading=async`
    script.async = true
    script.defer = true

    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)

    document.head.appendChild(script)
  })

  return window.__googleMapsLoadingPromise
}

export interface PlaceSuggestion {
  placeId?: string
  title: string
  address: string
  lat?: number
  lng?: number
  isSalzburg: boolean
  iconType?: 'airport' | 'station' | 'hotel' | 'pin'
}

/**
 * Determines appropriate icon based on place name and types
 */
function getPlaceIconType(title: string, types: string[] = []): 'airport' | 'station' | 'hotel' | 'pin' {
  const lower = title.toLowerCase()
  if (
    lower.includes('airport') ||
    lower.includes('flughafen') ||
    lower.includes('terminal') ||
    lower.includes('szg') ||
    lower.includes('muc') ||
    lower.includes('vie') ||
    types.includes('airport') ||
    types.includes('aerodrome')
  ) {
    return 'airport'
  }
  if (
    lower.includes('station') ||
    lower.includes('hauptbahnhof') ||
    lower.includes('bahnhof') ||
    lower.includes('hbf') ||
    types.includes('train_station') ||
    types.includes('transit_station')
  ) {
    return 'station'
  }
  if (
    lower.includes('hotel') ||
    lower.includes('resort') ||
    lower.includes('pension') ||
    lower.includes('suites') ||
    types.includes('lodging') ||
    types.includes('hotel')
  ) {
    return 'hotel'
  }
  return 'pin'
}

/**
 * Formats a clean, readable European address from geocoded properties.
 */
function formatEuropeanAddress(props: any): string {
  const parts: string[] = []

  if (props.street) {
    const street = props.housenumber ? `${props.street} ${props.housenumber}` : props.street
    parts.push(street)
  }

  const city = props.city || props.town || props.village || props.locality || props.district
  const postcode = props.postcode ? `${props.postcode} ` : ''

  if (city) {
    parts.push(`${postcode}${city}`.trim())
  }

  if (props.state && props.state !== city) {
    parts.push(props.state)
  }

  if (props.country) {
    parts.push(props.country)
  }

  return parts.length > 0 ? parts.join(', ') : props.name || 'Europe'
}

/**
 * Fetches real live place suggestions from Google Maps Places or high-speed live European Places API.
 * Never returns mock locations.
 */
export async function fetchPlaceSuggestions(
  query: string,
  options: {
    restrictToSalzburg: boolean
  }
): Promise<PlaceSuggestion[]> {
  const trimmed = query.trim()
  if (!trimmed || trimmed.length < 2) {
    return []
  }

  // 1. Try Google Maps Places Autocomplete if available
  await loadGoogleMapsScript()

  if (window.google?.maps?.places?.AutocompleteService) {
    try {
      if (!autocompleteServiceInstance) {
        autocompleteServiceInstance = new window.google.maps.places.AutocompleteService()
      }

      const request: any = {
        input: trimmed,
      }

      if (options.restrictToSalzburg) {
        request.locationRestriction = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(SALZBURG_BOUNDS.south, SALZBURG_BOUNDS.west),
          new window.google.maps.LatLng(SALZBURG_BOUNDS.north, SALZBURG_BOUNDS.east)
        )
        request.componentRestrictions = { country: 'at' }
      } else {
        request.locationBias = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(SALZBURG_BOUNDS.south, SALZBURG_BOUNDS.west),
          new window.google.maps.LatLng(SALZBURG_BOUNDS.north, SALZBURG_BOUNDS.east)
        )
      }

      const predictions: any[] = await new Promise((resolve) => {
        autocompleteServiceInstance.getPlacePredictions(
          request,
          (results: any[], status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              resolve(results)
            } else {
              resolve([])
            }
          }
        )
      })

      if (predictions && predictions.length > 0) {
        const mapped: PlaceSuggestion[] = predictions.map((pred) => {
          const title =
            pred.structured_formatting?.main_text ||
            pred.description.split(',')[0]
          const address =
            pred.structured_formatting?.secondary_text ||
            pred.description

          const inSalzburg = isLocationInSalzburg({
            title,
            address: pred.description,
          })

          return {
            placeId: pred.place_id,
            title,
            address,
            isSalzburg: inSalzburg,
            iconType: getPlaceIconType(title, pred.types || []),
          }
        })

        if (options.restrictToSalzburg) {
          const salzburgMatches = mapped.filter((item) => item.isSalzburg)
          if (salzburgMatches.length > 0) {
            return salzburgMatches
          }
        } else {
          return mapped
        }
      }
    } catch (err) {
      console.warn('Google Maps Autocomplete query failed, using live European geocoder:', err)
    }
  }

  // 2. High-speed Live European Places Search (Photon / OpenStreetMap Engine)
  return queryLiveEuropeanPlaces(trimmed, options.restrictToSalzburg)
}

/**
 * Queries real-time live place suggestions across Salzburg, Austria, and all European destinations.
 * Zero mock locations.
 */
async function queryLiveEuropeanPlaces(
  query: string,
  restrictToSalzburg: boolean
): Promise<PlaceSuggestion[]> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 4000)

    // Location bias centered on Salzburg so local results rank first, but entire Europe is searchable
    const searchUrl = `https://photon.komoot.io/api/?q=${encodeURIComponent(
      query
    )}&lat=${SALZBURG_CENTER.lat}&lon=${SALZBURG_CENTER.lng}&limit=12`

    const res = await fetch(searchUrl, { signal: controller.signal })
    clearTimeout(timeout)

    if (res.ok) {
      const data = await res.json()
      if (data?.features && Array.isArray(data.features)) {
        const results: PlaceSuggestion[] = data.features.map((feat: any) => {
          const props = feat.properties || {}
          const coords = feat.geometry?.coordinates || []
          const lng = coords[0]
          const lat = coords[1]

          const title = props.name || props.street || query
          const address = formatEuropeanAddress(props)

          const inSalzburg = isLocationInSalzburg({
            title,
            address,
            lat,
            lng,
          })

          return {
            placeId: `${props.osm_type || 'P'}-${props.osm_id || Math.random()}`,
            title,
            address,
            lat,
            lng,
            isSalzburg: inSalzburg,
            iconType: getPlaceIconType(title, [props.osm_key, props.osm_value, props.type]),
          }
        })

        if (restrictToSalzburg) {
          const filtered = results.filter((item) => item.isSalzburg)
          if (filtered.length > 0) {
            return filtered.slice(0, 8)
          }
        } else {
          return results.slice(0, 8)
        }
      }
    }
  } catch {
    // If request fails or aborts
  }

  // Fallback to direct Nominatim if needed
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3500)

    const nomUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      query
    )}&addressdetails=1&limit=6`

    const res = await fetch(nomUrl, { signal: controller.signal })
    clearTimeout(timeout)

    if (res.ok) {
      const list = await res.json()
      if (Array.isArray(list)) {
        const results: PlaceSuggestion[] = list.map((item: any) => {
          const title = item.name || item.display_name.split(',')[0]
          const address = item.display_name
          const lat = parseFloat(item.lat)
          const lng = parseFloat(item.lon)

          const inSalzburg = isLocationInSalzburg({
            title,
            address,
            lat,
            lng,
          })

          return {
            placeId: item.place_id?.toString(),
            title,
            address,
            lat,
            lng,
            isSalzburg: inSalzburg,
            iconType: getPlaceIconType(title, [item.type, item.class]),
          }
        })

        if (restrictToSalzburg) {
          return results.filter((r) => r.isSalzburg)
        }
        return results
      }
    }
  } catch {
    // Fallback error
  }

  return []
}

/**
 * Resolves full geographic details for a selected place.
 */
export async function getPlaceLocationDetails(suggestion: PlaceSuggestion): Promise<LocationGeoData> {
  if (suggestion.lat && suggestion.lng) {
    return {
      title: suggestion.title,
      address: suggestion.address,
      lat: suggestion.lat,
      lng: suggestion.lng,
      isSalzburg: suggestion.isSalzburg,
      placeId: suggestion.placeId,
    }
  }

  if (suggestion.placeId && window.google?.maps?.places?.PlacesService) {
    try {
      if (!placesServiceInstance) {
        const dummyDiv = document.createElement('div')
        placesServiceInstance = new window.google.maps.places.PlacesService(dummyDiv)
      }

      const details: any = await new Promise((resolve) => {
        placesServiceInstance.getDetails(
          {
            placeId: suggestion.placeId,
            fields: ['geometry', 'formatted_address', 'name', 'address_components'],
          },
          (result: any, status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
              resolve(result)
            } else {
              resolve(null)
            }
          }
        )
      })

      if (details?.geometry?.location) {
        const lat = details.geometry.location.lat()
        const lng = details.geometry.location.lng()
        const fullAddress = details.formatted_address || suggestion.address
        const isSalzburg = isLocationInSalzburg({
          title: details.name || suggestion.title,
          address: fullAddress,
          lat,
          lng,
        })

        return {
          title: details.name || suggestion.title,
          address: fullAddress,
          lat,
          lng,
          isSalzburg,
          placeId: suggestion.placeId,
        }
      }
    } catch {
      // Fall through
    }
  }

  return {
    title: suggestion.title,
    address: suggestion.address,
    isSalzburg: suggestion.isSalzburg,
    placeId: suggestion.placeId,
  }
}
